import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
});

export const fetchProducts = () => api.get('/products').then(r => r.data);
export const subscribeNewsletter = (email) =>
  api.post('/newsletter', { email }).then(r => r.data);
export const submitContact = (payload) =>
  api.post('/contact', payload).then(r => r.data);
export const sendAdvisorMessage = (payload) =>
  api.post('/advisor/chat', payload).then(r => r.data);

// --- Formulas / Cost-Plus-25 ---
export const fetchFormulas = () => api.get('/formula').then(r => r.data);
export const fetchFormula = (slug) =>
  api.get(`/formula/${encodeURIComponent(slug)}`).then(r => r.data);
export const fetchFormulaCostBreakdown = (slug, volumeMl) =>
  api
    .get(`/formula/${encodeURIComponent(slug)}/cost-breakdown`, {
      params: volumeMl ? { volume_ml: volumeMl } : {},
    })
    .then(r => r.data);

// --- Brand identity & Admin identity / Notifications ---
export const fetchBrandIdentity = () => api.get('/brand').then(r => r.data);
export const fetchAdminIdentity = () => api.get('/admin/identity').then(r => r.data);
export const fetchAdminImpact = () => api.get('/admin/impact').then(r => r.data);
export const fetchAdminNotifications = (limit = 50, kind) =>
  api
    .get('/admin/notifications', { params: { limit, ...(kind ? { kind } : {}) } })
    .then(r => r.data);

// --- Genesis Guardians ---
export const fetchGuardiansCount = () => api.get('/guardians/count').then(r => r.data);
export const fetchVesselUnits    = () => api.get('/vessels/units').then(r => r.data);
export const registerGuardian = (email, referralSource, referrerEmail) =>
  api
    .post('/guardians', {
      email,
      referral_source: referralSource,
      referrer_email:  referrerEmail || undefined,
    })
    .then(r => r.data);
export const fetchGuardianProgress = (email) =>
  api.get(`/guardians/by-email/${encodeURIComponent(email)}`).then(r => r.data);

// --- Partner B2B Lead capture (Oracle Hero) ---
export const capturePartnerLead = ({ email, house }) =>
  api.post('/partners/leads', { email, house }).then(r => r.data);
export const fetchPartnerLeadsCount = () =>
  api.get('/partners/leads/count').then(r => r.data);

// --- Provenance (Living Vessel evidence + on-chain anchor) ---
export const fetchProvenanceStage = (stageId, batchId = 'BATCH-042') =>
  api
    .get(`/provenance/${encodeURIComponent(stageId)}`, { params: { batch_id: batchId } })
    .then(r => r.data);
export const fetchChainWallet = () =>
  api.get('/provenance/wallet').then(r => r.data);
export const anchorBatchOnChain = (batchId = 'BATCH-042') =>
  api.post('/provenance/anchor', { batch_id: batchId }).then(r => r.data);

// --- Inbound Purity Shield (raw-material drums) ---
export const fetchInboundDrums = (status) =>
  api.get('/inbound/drums', { params: status ? { status } : {} }).then(r => r.data);
export const scanInboundDrum = (drumId, operator) =>
  api
    .post(`/inbound/scan/${encodeURIComponent(drumId)}`, null, {
      params: operator ? { operator } : {},
    })
    .then(r => r.data);
export const breakInboundSeal = (drumId, batchId, operator) =>
  api
    .post('/inbound/break-seal', { drum_id: drumId, batch_id: batchId, operator })
    .then(r => r.data);
export const fetchInboundBatch = (batchId) =>
  api.get(`/inbound/batches/${encodeURIComponent(batchId)}`).then(r => r.data);


// --- Sevagram ---
export const fetchSevagramPosts = (params = {}) =>
  api.get('/sevagram/posts', { params }).then(r => r.data);
export const fetchSevagramCreators = () =>
  api.get('/sevagram/creators').then(r => r.data);
export const fetchSevagramLeaderboard = (limit = 5) =>
  api.get('/sevagram/leaderboard', { params: { limit } }).then(r => r.data);
export const applySevagramCreator = (payload) =>
  api.post('/sevagram/creators', payload).then(r => r.data);
export const createOrder = (payload) =>
  api.post('/orders', payload).then(r => r.data);
export const fetchCreatorDashboard = (handle) =>
  api.get(`/sevagram/dashboard/${encodeURIComponent(handle)}`).then(r => r.data);
export const trackPostEvent = (postId, kind, channel) =>
  api
    .post(`/sevagram/posts/${postId}/track`, { kind, channel })
    .then(r => r.data)
    .catch(() => null); // telemetry — never block UX

// --- Partners / B2B ---
export const fetchPartners = () =>
  api.get('/partners').then(r => r.data);
export const applyPartner = (payload) =>
  api.post('/partners', payload).then(r => r.data);
export const fetchPartnerDashboard = (partnerId) =>
  api.get(`/partners/${partnerId}/dashboard`).then(r => r.data);
export const refillHandshake = (payload) =>
  api.post('/refills/handshake', payload).then(r => r.data);
export const logPartnerUsage = (partnerId, payload) =>
  api.post(`/partners/${partnerId}/usage`, payload).then(r => r.data);
export const submitBulkOrder = (partnerId, payload) =>
  api.post(`/partners/${partnerId}/bulk-order`, payload).then(r => r.data);

// --- Digital Twin ---
export const fetchBottleTwin = (bottleId) =>
  api.get(`/twins/${encodeURIComponent(bottleId)}`).then(r => r.data);
export const pushTwinSensor = (bottleId, payload) =>
  api.post(`/twins/${encodeURIComponent(bottleId)}/sensor`, payload).then(r => r.data);

// --- Admin / Dharma Command Center ---
export const fetchDharmaReport = () => api.get('/admin/dharma').then(r => r.data);
export const fetchCostAudit    = () => api.get('/admin/cost-audit').then(r => r.data);
export const fetchVessels      = () => api.get('/admin/vessels').then(r => r.data);
export const fetchBatches      = () => api.get('/admin/batches').then(r => r.data);
export const fetchPartnerQueue = () => api.get('/admin/partners/queue').then(r => r.data);
export const approvePartner    = (partnerId) =>
  api.post(`/partners/${partnerId}/approve`).then(r => r.data);
export const fetchAdminPosts   = () => api.get('/admin/sevagram/posts').then(r => r.data);
export const setPostVisibility = (postId, visible) =>
  api.post(`/admin/sevagram/posts/${postId}/visibility`, { visible }).then(r => r.data);
